angular.module('createReferrals')
    .service('ReferralHelper', ['$uibModal', '$q', 'ModalHandler', 'Practice', 'ProviderInvitation', 'Spinner', 'UnsavedChanges',
    function ($uibModal, $q, ModalHandler, Practice, ProviderInvitation, Spinner, UnsavedChanges) {
        // A queued note goes out with these fields only (DCL-634). The rest of
        // the local object is display state: created_at/updated_at (timestamps
        // are the server's; Rails 8 answers 500 to the epoch milliseconds a new
        // note carries), the author's name, versions.
        var NOTE_FIELDS = ['id', 'message', 'user_id'];
        // Payload copy -> the queued note it was made from, so syncSavedNotes
        // can drain exactly the entries that went out in a request.
        var noteSources = new WeakMap();

        function noteForSave(note) {
            var copy = {};
            NOTE_FIELDS.forEach(function (key) {
                if (note[key] !== undefined && note[key] !== null && note[key] !== '') { copy[key] = note[key]; }
            });
            noteSources.set(copy, note);
            return copy;
        }

        return {

            // ---- DCL-369 live-draft autosave helpers ----

            // Payload for autosave. Unlike the old prepareSubmit, every part is
            // optional — drafts are partial by design. Keys left undefined are
            // dropped by angular.toJson, so absent values never overwrite server
            // state; explicit nulls (e.g. a cleared provider) do clear it.
            buildDraftPayload: function (scope) {
                var referral = scope.model.referral;
                return {
                    referral: {
                        orig_practice_id: referral.orig_practice_id || scope.auth.practice_id || undefined,
                        orig_provider_id: referral.orig_provider_id,
                        orig_provider_address_id: referral.orig_provider_address_id,
                        dest_practice_id: scope.destinationPractice ? scope.destinationPractice.id : referral.dest_practice_id,
                        dest_provider_id: referral.dest_provider_id,
                        dest_provider_invited_id: referral.dest_provider_invited_id,
                        procedure_id: referral.procedure_id,
                        address_id: referral.address_id,
                        memo: referral.memo,
                        patient_id: (scope.patient && scope.patient.id) || referral.patient_id || undefined,
                        teeth: (scope.teeth || []).join('+'),
                        notes_attributes: (referral.notes_attributes || []).map(noteForSave)
                    }
                };
            },

            // Guest variant: identity comes from the created guest user, and the
            // patient is sent as raw fields (the backend creates/updates the row).
            buildGuestDraftPayload: function (scope) {
                var guest = scope.guest || {};
                // Notes are authored by the guest, whose user record may not have
                // existed when the note was added locally. Attribute the local
                // notes (the list shows their author) before copying them out.
                (scope.model.referral.notes_attributes || []).forEach(function (note) {
                    if (!note.user_id) { note.user_id = guest.id; }
                    if (note.user) {
                        if (!note.user.first_name) { note.user.first_name = guest.first_name; }
                        if (!note.user.last_name) { note.user.last_name = guest.last_name; }
                    }
                });
                var payload = this.buildDraftPayload(scope);
                payload.referral.orig_practice_id = guest.practice_id || undefined;
                payload.referral.orig_provider_id = guest.id || undefined;
                delete payload.referral.patient_id; // linkage happens server-side
                var patient = scope.patient || {};
                if (patient.first_name || patient.last_name || patient.birthday || patient.phone) {
                    payload.patient = {
                        first_name: patient.first_name,
                        last_name: patient.last_name,
                        birthday: patient.birthday,
                        phone: patient.phone
                    };
                }
                return payload;
            },

            // After a successful autosave the server's notes are the truth
            // (DCL-636). The queue entries that were in the sent payload are
            // saved now, so they leave the queue. The list becomes the server's
            // notes (real ids, server timestamps, author), with a still-queued
            // edit shown in place of its server version so it doesn't revert,
            // then the queued notes the server hasn't seen yet. A later edit
            // starts from the server object, so it carries the right id; only
            // an edit dialog already open on a sent note needs its id handed
            // back (below). The patient-change activity rows the review screen
            // mixes into the list stay.
            syncSavedNotes: function (scope, data, sentPayload) {
                var referral = scope.model.referral;
                var sentCopies = (sentPayload && sentPayload.referral && sentPayload.referral.notes_attributes) || [];
                var sent = sentCopies.map(function (note) { return noteSources.get(note) || note; });
                var queued = (referral.notes_attributes || []).filter(function (note) {
                    return sent.indexOf(note) === -1;
                });
                referral.notes_attributes = queued;
                if (!data || !angular.isArray(data.notes)) { return; }

                // An edit dialog opened on a sent new note before this answer
                // came back still holds the queued object (NotesController).
                // Give it the id of the note the server made from it, matched by
                // message among the notes this save created, so the edit updates
                // that note rather than add a second one.
                var known = (referral.notes || []).concat(queued)
                    .filter(function (note) { return note.id && !note.trackable_id; })
                    .map(function (note) { return note.id; });
                var created = data.notes.filter(function (serverNote) { return known.indexOf(serverNote.id) === -1; });
                sentCopies.forEach(function (copy, i) {
                    if (copy.id || sent[i] === copy) { return; }
                    var match = created.filter(function (serverNote) { return serverNote.message === copy.message; })[0];
                    if (!match) { return; }
                    created.splice(created.indexOf(match), 1);
                    sent[i].id = match.id;
                });

                var shown = data.notes.map(function (serverNote) {
                    var edit = queued.filter(function (note) { return note.id && note.id === serverNote.id; })[0];
                    return edit || serverNote;
                });
                queued.forEach(function (note) {
                    if (shown.indexOf(note) === -1) { shown.push(note); }
                });
                var activities = (referral.notes || []).filter(function (note) { return note.trackable_id; });
                referral.notes = shown.concat(activities);
            },

            // Shared wiring for live-draft screens: change detection (gated on
            // form.$dirty so programmatic initialization never creates a draft),
            // blur/navigation flushes, the minimal unsaved-changes guard and the
            // retry handler for the saving indicator.
            wireAutosave: function (scope, autosave, buildPayload) {
                scope.$watch(function () {
                    return angular.toJson(buildPayload(scope));
                }, function (newVal, oldVal) {
                    if (newVal === oldVal) { return; } // watcher initialization
                    if (!scope.form || !scope.form.$dirty) { return; } // not a user edit
                    autosave.notifyChange();
                });

                var flushListener = function () { autosave.flush(); };
                var formElement = angular.element(document.getElementById('formCreateReferral'));
                formElement.on('focusout', flushListener);
                scope.$on('$destroy', function () { formElement.off('focusout', flushListener); });
                scope.$on('$stateChangeStart', flushListener);

                UnsavedChanges.setCbHaveUnsavedChanges(function () {
                    return autosave.hasPendingChanges();
                });

                scope.retryAutosave = function () { autosave.retry(); };
            },

            // Upload anything still sitting in the uploader queue (guest flow: files
            // added before the guest identity existed). Items get the draft id and
            // token headers exactly like immediate uploads.
            uploadQueuedAttachments: function (scope, referralId) {
                if (!scope.uploader) { return; }
                scope.uploader.queue.forEach(function (item) {
                    if (item.isUploading || item.isUploaded) { return; }
                    var hasReferralId = item.formData.some(function (entry) { return 'referral_id' in entry; });
                    if (hasReferralId) { return; }
                    item.formData.push({referral_id: referralId});
                    if (scope.draftUploadHeaders) {
                        item.headers = angular.extend({}, item.headers, scope.draftUploadHeaders());
                    }
                    item.upload();
                });
            },

            // ---- DCL-477 attachment date editing ----

            // May the signed-in user correct the date an attached image was taken?
            // Only the practice that uploaded it may, and that has to hold after a
            // refresh — so the answer comes from the serialized author, not from a
            // client-only "just uploaded" flag or the transient `invalid` marker.
            // Guests (no practice of their own) and attachments serialized without
            // an author both get false. Shared by the create/review page
            // (AttachmentsController) and view-referral (ViewReferralsController).
            canEditAttachmentDate: function (auth, attachment) {
                var viewerPracticeId = auth && auth.practice_id;
                var authorPracticeId = attachment && attachment.author && attachment.author.practice_id;
                if (!viewerPracticeId || !authorPracticeId) { return false; }
                return viewerPracticeId === authorPracticeId;
            },

            watchProviders: function (scope) {
                var self = this;
                var findProvider = function (id) {
                    if (!scope.destinationPractice || !scope.destinationPractice.users){
                        return {};
                    }
                    var users = scope.destinationPractice.users;
                    var usersLength = users.length;
                    for (var i = 0; i < usersLength; i++) {
                        if (id == users[i].id) {
                            return users[i];
                        }
                    }
                    return {};
                };
                //initial check if current referral has valid (not removed) dest provider
                scope.$watch(function(){
                    return scope.form.provider;
                },function(newVal, oldVal, currentScope){
                    if (newVal){
                        if(findProvider(currentScope.model.referral.dest_provider_id).removed_at){
                            currentScope.form.provider.$setValidity('userActive', false);
                        }
                    }

                });

                //initial check if current referral has valid (not removed) dest provider
                scope.$watch(function(){
                    return scope.form.provider_invited;
                },function(newVal, oldVal, currentScope){
                    if (newVal){
                        if(findProvider(currentScope.model.referral.dest_provider_invited_id).removed_at){
                            currentScope.form.provider_invited.$setValidity('userActive', false);
                        }
                    }

                });


                scope.$watch( //observing changes of the dest_provider_invited_id field
                    function () {
                        return scope.model.referral.dest_provider_invited_id; //we are watching exactly this 'dest_provider_invited_id' property, not the whole 'model' object
                    }, function (newVal, oldVal, currentScope) {
                        if (newVal) {
                            if(currentScope.form.provider_invited){
                                currentScope.form.provider_invited.$setValidity('userActive', findProvider(newVal).removed_at? false : true);
                            }
                            currentScope.model.referral.dest_provider_id = null; //if dest_provider_invited_id is set (user added new provider invitation to the referral), we need to remove 'dest_provider_id'
                        }
                    });

                scope.$watchGroup([ //observing changes of the dest_provider_id field
                    function () {
                        return scope.model.referral; // the referral itself, to tell a swap from an edit (DCL-682)
                    },
                    function () {
                        return scope.model.referral.dest_provider_id; //we are watching exactly this 'dest_provider_id' property, not the whole 'model' object
                    }],
                    function (newValues, oldValues, currentScope) {
                        var newVal = newValues[1];
                        // A referral swapped in whole, like a restored guest draft, brings
                        // its own type and procedure; only a provider picked on the same
                        // referral re-derives the type from the provider's specialty (DCL-682).
                        var referralSwapped = newValues[0] !== oldValues[0];
                        if (newVal) {
                            if(currentScope.form.provider){
                                currentScope.form.provider.$setValidity('userActive', findProvider(newVal).removed_at ? false : true );
                            }
                            if(!referralSwapped && scope.destinationPractice.practice_type && scope.destinationPractice.practice_type.code === 'multi_specialty') {
                                var selectedProvider = findProvider(currentScope.model.referral.dest_provider_id);
                                if(currentScope.practiceTypes && selectedProvider.specialty_type_id) {
                                    self.updatePracticeType(currentScope, selectedProvider.specialty_type_id);
                                } else if(scope.practiceType && scope.practiceType.id != scope.destinationPractice.practice_type_id) {
                                    self.updatePracticeType(currentScope, scope.destinationPractice.practice_type_id);
                                }
                            }
                            currentScope.model.referral.dest_provider_invited_id = null; //if dest_provider_id is set (user selected existing referral from the dropdown), we need to remove 'dest_provider_invited_id'
                        }
                    });
            },
            updatePracticeType: function (scope, practice_type_id) {
                this.selectPracticeType(scope, function () { return practice_type_id; });
            },
            // pick(practiceTypes) returns the id of the type to select. The
            // procedure block loads the type list and may still be on its way:
            // the latest pick then waits for applyPendingPracticeType (DCL-682).
            selectPracticeType: function (scope, pick) {
                if (!(scope.practiceTypes && scope.practiceTypes.length)) {
                    scope.pendingPracticeTypePick = pick;
                    return;
                }
                scope.pendingPracticeTypePick = null;
                var practice_type_id = pick(scope.practiceTypes);
                for (var i = 0; i < scope.practiceTypes.length; i++) {
                    if (scope.practiceTypes[i].id == practice_type_id) {
                        scope.practiceType = scope.practiceTypes[i];
                        break;
                    }
                }
            },
            // The listed type that offers this procedure, if any.
            practiceTypeIdOfProcedure: function (practiceTypes, procedureId) {
                if (!procedureId) { return null; }
                for (var i = 0; i < practiceTypes.length; i++) {
                    var procedures = practiceTypes[i].procedures || [];
                    for (var j = 0; j < procedures.length; j++) {
                        if (procedures[j].id == procedureId) { return practiceTypes[i].id; }
                    }
                }
                return null;
            },
            applyPendingPracticeType: function (scope) {
                if (scope.pendingPracticeTypePick) {
                    this.selectPracticeType(scope, scope.pendingPracticeTypePick);
                }
            }
        }
    }]);
